import React from 'react';
import { useLazyQuery } from '@apollo/client';
import { format } from 'date-fns';
import omit from 'lodash/omit';

import { makeStyles } from '@material-ui/core/styles';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import DeleteIcon from '@material-ui/icons/Delete';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';

import { useUser } from 'actions/useUser';
import { Analysis, Recording, Role } from 'types/schema.type';
import { RecordingChart } from './RecordingChart';
import { GET_RECORDING_ANNOTATIONS, GET_RECORDING_DOWNLOAD_URL } from '../queries';
import { downloadUri, downloadString } from '../utilities';
import { analysedAtLabel } from '../utilities/analysedAt';
import { FlagSelector } from './FlagSelector';

const useRowStyles = makeStyles({
  root: {
    '& > *': {
      borderBottom: 'unset',
    },
  },
  date: {
    minWidth: 110,
  },
  hideOverflow: {
    display: 'block',
    width: '100px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

/** Shown in the Analysis Status column while a reanalysis is running (#508). */
export const ANALYSIS_IN_PROGRESS = 'Analysing…';

function getAnalysisStatus(analysis: Analysis | undefined) {
  if (!analysis) return 'Not analyzed';

  if (analysis.success) {
    // project_management#533: a failed reanalysis keeps the last good result
    // and stamps lastFailedAnalysisAt. A bare "Success" here would silently
    // hide the failure — precisely the bug being fixed.
    if (analysis.lastFailedAnalysisAt) {
      const failedAt = format(analysis.lastFailedAnalysisAt, 'yyy-MM-dd H:mm');
      const reason = analysis.lastAnalysisError ?? 'unknown error';

      return `Reanalysis failed ${failedAt} (${reason}) — showing previous result`;
    }

    return 'Success';
  }

  return analysis.error ?? 'Error';
}

type RecordingItemProps = Partial<Recording> & {
  recordingId: Recording['id'];
  flagOptions: string[];
  updatedAt: number | undefined;
  onDelete: (param: any) => void;
  /**
   * Requests a reanalysis and reports the outcome to the user. Owned by the
   * parent so one snackbar serves the whole table (project_management#508).
   */
  onReanalyze: (args: {
    recordingId: Recording['id'];
    fileName?: string | null;
  }) => Promise<void> | void;
};

export const RecordingItem = React.memo((props: RecordingItemProps) => {
  const {
    recordingId,
    analysis,
    channelCount,
    comment,
    fileName,
    flagOptions,
    flags,
    updatedAt,
    isBeingAnalysed,
    recordingStartedAt,
    onDelete,
    onReanalyze,
  } = props;

  const { user } = useUser();

  const classes = useRowStyles();

  const [open, setOpen] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [isRequestPending, setIsRequestPending] = React.useState(false);

  const isUserRole = user?.role === Role.User;
  const isClinicRole = user?.role === Role.Clinic;

  const [getAnnotations, { data: annotationData }] = useLazyQuery(GET_RECORDING_ANNOTATIONS, {
    variables: { recordingId },
    onCompleted: (data) => {
      const annotations = data?.getMyRecording?.analysis?.annotations;

      if (annotations) {
        const cleaned = annotations.map((it: any) => omit(it, '__typename'));
        const uri = JSON.stringify(cleaned, null, 4);

        downloadString(uri, 'annotations.json');
      } else {
        alert('Failed to download annotations');
      }
    },
  });

  const [getDownloadUri, { data: uriData }] = useLazyQuery(GET_RECORDING_DOWNLOAD_URL, {
    variables: { recordingId },
    onCompleted: (data) => {
      const uri = data?.getMyRecording?.downloadUrl;

      if (uri) {
        downloadUri(uri, fileName);
      } else {
        alert('Failed to download file');
      }
    },
  });

  const handleAnnotationDownload = () => {
    const annotations = annotationData?.getMyRecording?.analysis?.annotations;

    if (annotations) {
      const cleaned = annotations.map((it: any) => omit(it, '__typename'));
      const uri = JSON.stringify(cleaned, null, 4);

      downloadString(uri, 'annotations.json');
    } else {
      getAnnotations();
    }
  };

  const handleFileDownload = () => {
    const uri = uriData?.getMyRecording?.downloadUrl;

    if (uri) {
      // takes uri from cache
      downloadUri(uri, fileName);
    } else {
      // if cache empty, refetch
      getDownloadUri();
    }
  };

  /**
   * project_management#508: the click used to fire the mutation and ignore
   * both its payload and its errors, so it had no visible consequence at all.
   * The parent now owns reporting; this keeps a local pending flag so the row
   * shows the request landed before the list refetch comes back.
   */
  const handleReanalyze = async () => {
    if (isUserRole) return;

    setIsRequestPending(true);

    try {
      await onReanalyze({ recordingId, fileName });
    } finally {
      setIsRequestPending(false);
    }
  };

  const handleDelete = () => {
    if (isUserRole || isClinicRole) return;

    try {
      // show delete recording confirmation
      onDelete({
        variables: {
          deleteRecordingRecordingId: recordingId,
        },
      });
    } catch (e) {
      alert('Failed to delete');
    }
  };

  /**
   * In flight = this row just requested one, or the server says one is running
   * (`isBeingAnalysed`, set when the request is published and cleared by the
   * result listener).
   */
  const isAnalysisInFlight = isRequestPending || Boolean(isBeingAnalysed);
  const analysisStatus = isAnalysisInFlight ? ANALYSIS_IN_PROGRESS : getAnalysisStatus(analysis);
  const rPeaksCount = analysis?.rpeaksCount ?? 0;

  return (
    <>
      <TableRow className={classes.root} selected={open} hover>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell className={classes.date} component="th" scope="row" align="center">
          {recordingStartedAt ? format(recordingStartedAt, 'yyy-MM-dd H:mm:ss') : 'NA'}
        </TableCell>
        <TableCell className={classes.date} component="th" scope="row" align="center">
          {updatedAt ? format(updatedAt, 'yyy-MM-dd H:mm:ss') : ''}
        </TableCell>
        {/* project_management#507: when the analysis on screen was produced. */}
        <TableCell
          className={classes.date}
          component="th"
          scope="row"
          align="center"
          data-testid="analysed-at"
        >
          {analysedAtLabel(analysis)}
        </TableCell>
        <TableCell align="center">{channelCount}</TableCell>
        <TableCell align="center">{fileName}</TableCell>
        <TableCell align="center">
          <Tooltip title={analysisStatus}>
            <div className={classes.hideOverflow}>{analysisStatus}</div>
          </Tooltip>
        </TableCell>
        <TableCell>{rPeaksCount}</TableCell>
        <TableCell align="center">
          <Tooltip title={comment || ''}>
            <div className={classes.hideOverflow}>{comment}</div>
          </Tooltip>
        </TableCell>
        <TableCell align="center">
          <FlagSelector recordingId={recordingId} flags={flags} flagOptions={flagOptions} disabled={isUserRole} />
        </TableCell>
        <TableCell align="center">
          <IconButton
            color="primary"
            aria-label="download annotations"
            onClick={handleAnnotationDownload}
          >
            <CloudDownloadIcon />
          </IconButton>
        </TableCell>
        <TableCell align="center">
          <IconButton color="primary" aria-label="download file" onClick={handleFileDownload}>
            <CloudDownloadIcon />
          </IconButton>
        </TableCell>
        <TableCell align="center">
          {/*
            Deliberately still clickable while in flight: a lost request leaves
            `isBeingAnalysed` true forever (27 recordings are stuck that way,
            see project_management#510), and disabling on it would lock the row
            out of the only action that can recover it.
            The span keeps the tooltip working when the button IS disabled.
          */}
          <Tooltip
            title={
              isAnalysisInFlight
                ? 'Analysis in progress — you can request it again if it does not finish'
                : 'Reanalyze'
            }
          >
            <span>
              <IconButton
                color="primary"
                aria-label="reanalyze"
                disabled={isUserRole}
                onClick={handleReanalyze}
              >
                {isAnalysisInFlight ? (
                  <CircularProgress size={20} aria-label="analysis in progress" />
                ) : (
                  <AutorenewIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
        <TableCell align="center">
          <IconButton
            color="secondary"
            aria-label="delete file"
            disabled={isUserRole || isClinicRole}
            onClick={() => setIsDialogOpen(true)}
          >
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={17}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <RecordingChart recordingId={recordingId} />
          </Collapse>
        </TableCell>
      </TableRow>

      <Dialog open={isDialogOpen} PaperComponent={Paper} aria-labelledby="draggable-dialog-title">
        <DialogTitle>Delete recording</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete Recording ID <strong>{recordingId}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" autoFocus onClick={() => setIsDialogOpen(false)}>
            Cancel
          </Button>
          <Button color="secondary" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

RecordingItem.displayName = 'RecordingItem';
